/* eslint-disable @typescript-eslint/no-empty-function */
import { WorldMap_worldMap } from 'graphql/query/__generated__/WorldMap';
import { HistoryRecord } from 'libs/types/history/historyTypes';
import { revertHistoryOperation } from 'libs/utils/logic/history/historyUtils';
import { createContext, useState } from 'react';
import { IWorldMarker } from 'react-give-me-map';

export interface IEditHistoryState {
    markerHistory: HistoryRecord[];
    serverQueue: HistoryRecord[];
    markerHistoryPointer: number | null;
}

export interface IEditHistoryActions {
    addHistoryRecord: (historyValue: HistoryRecord) => void;
    removeFromServerQueue: (idsToRemove: string[]) => void;
    goForwardInHistory: () => void;
    goBackInHistory: () => void;
}

export type ICtxHistory = [IEditHistoryState, IEditHistoryActions];

export const CtxHistory = createContext<ICtxHistory>(null);

export const useEditHistory = (params: {
    addMarker: (marker: IWorldMarker) => void;
    updateMarker: (id: string, marker: IWorldMarker) => Promise<void>;
    removeMarkers: (ids: string[]) => void;
    reorder: (order: Array<{ worldMapMarkerId: string; order: number }>) => void;
    updateMap: (map: Partial<WorldMap_worldMap>) => void;
}): ICtxHistory => {
    const [state, setInnerState] = useState<{
        history: HistoryRecord[];
        pointer: number | null;
        serverQueue: HistoryRecord[];
    }>({
        history: [],
        pointer: null,
        serverQueue: []
    });

    function addHistoryRecord(historyValue: HistoryRecord) {
        setInnerState((innerState) => {
            const newMarkerHistory = [
                ...innerState.history.slice(0, innerState.pointer + 1),
                historyValue
            ];
            return {
                history: newMarkerHistory,
                pointer: newMarkerHistory.length - 1,
                serverQueue: [...innerState.serverQueue, historyValue]
            };
            // }
        });
    }

    function removeFromServerQueue(idsToRemove: string[]) {
        setInnerState((innerState) => ({
            history: innerState.history,
            pointer: innerState.pointer,
            serverQueue: innerState.serverQueue.filter((e) => !idsToRemove.includes(e.recordId))
        }));
    }

    function moveHistoryPointer(newValue: number) {
        const oldMarkerHistoryPointer = state.pointer;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const getUpdate = (): any => {
            if (oldMarkerHistoryPointer === null || oldMarkerHistoryPointer < newValue) {
                return state.history.slice(
                    oldMarkerHistoryPointer === null ? 0 : oldMarkerHistoryPointer + 1,
                    newValue + 1
                );
            }

            return state.history
                .slice(newValue === null ? 0 : newValue + 1, oldMarkerHistoryPointer + 1)
                .reverse()
                .map(revertHistoryOperation);
        };

        setInnerState((state) => ({
            history: state.history,
            pointer: newValue,
            serverQueue: [...state.serverQueue, ...getUpdate()]
        }));
    }

    function goForwardInHistory() {
        if (!state.history.length || state.pointer === state.history.length - 1) {
            return;
        }

        const historyRecord = state.history[state.pointer === null ? 0 : state.pointer + 1];

        if (historyRecord.operation === 'add') {
            params.addMarker(historyRecord.data);
        }
        if (historyRecord.operation === 'update') {
            params.updateMarker(historyRecord.data.id, historyRecord.data);
        }
        if (historyRecord.operation === 'delete') {
            params.removeMarkers([historyRecord.data.id]);
        }
        if (historyRecord.operation === 'reorder') {
            params.reorder(historyRecord.data);
        }
        if (historyRecord.operation === 'updateMap') {
            params.updateMap(historyRecord.data);
        }
        moveHistoryPointer(state.pointer === null ? 0 : state.pointer + 1);
    }

    function goBackInHistory() {
        if (state.pointer === null) {
            return;
        }

        const historyRecord = state.history[state.pointer];

        if (historyRecord.operation === 'add') {
            params.removeMarkers([historyRecord.data.id]);
        }
        if (historyRecord.operation === 'update') {
            params.updateMarker(historyRecord.data.id, historyRecord.previousData);
        }
        if (historyRecord.operation === 'delete') {
            params.addMarker(historyRecord.data);
        }
        if (historyRecord.operation === 'reorder') {
            params.reorder(historyRecord.previousData);
        }
        if (historyRecord.operation === 'updateMap') {
            params.updateMap(historyRecord.previousData);
        }
        moveHistoryPointer(state.pointer === 0 ? null : state.pointer - 1);
    }

    return [
        {
            markerHistory: state.history,
            serverQueue: state.serverQueue,
            markerHistoryPointer: state.pointer
        },
        {
            addHistoryRecord,
            removeFromServerQueue,
            goForwardInHistory,
            goBackInHistory
        }
    ];
};
